import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import './_whatsAppModal.scss';
import { useSelector } from 'react-redux';

const BUSINESS_TYPES = [
  { value: 'OTHER', label: 'Other' },
  { value: 'AUTO', label: 'Automotive' },
  { value: 'BEAUTY', label: 'Beauty' },
  { value: 'APPAREL', label: 'Apparel & Fashion' },
  { value: 'EDU', label: 'Education' },
  { value: 'ENTERTAIN', label: 'Entertainment' },
  { value: 'EVENT_PLAN', label: 'Event Planning' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'GROCERY', label: 'Grocery' },
  { value: 'GOVT', label: 'Government' },
  { value: 'HOTEL', label: 'Hotel & Lodging' },
  { value: 'HEALTH', label: 'Healthcare' },
  { value: 'NONPROFIT', label: 'Non-Profit' },
  { value: 'PROF_SERVICES', label: 'Professional Services' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'ALCOHOL', label: 'Alcohol' },
  { value: 'ONLINE_GAMBLING', label: 'Online Gambling' },
  { value: 'PHYSICAL_GAMBLING', label: 'Physical Gambling' },
  { value: 'OTC_DRUGS', label: 'OTC Drugs' },
];

const UpdateBusinessModal = ({ show, onHide, initialData, onSubmit }) => {
  const { access_token } = useSelector((state) => state.auth.user);
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.vertical) {
      newErrors.vertical = 'Business type is required';
    }

    if (!formData.about.trim()) {
      newErrors.about = 'About information is required';
    }

    // Check if websites is an array and validate the first URL if it exists
    if (Array.isArray(formData.websites) && formData.websites.length > 0) {
      const websiteUrl = formData.websites[0];
      if (websiteUrl && !websiteUrl.match(/^https?:\/\/.+/)) {
        newErrors.websites = 'Website URL must start with https://';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitData = {
        ...formData,
        websites: formData.websites ? [formData.websites] : [],
      };
      onSubmit(submitData);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="business-profile-modal"
      centered
      className="wa-business-profile-modal"
    >
      <Modal.Header closeButton className="bg-light">
        <Modal.Title id="business-profile-modal">Business Profile Settings</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group className="wa-form-group">
                <Form.Label>Messaging Product</Form.Label>
                <Form.Control
                  type="text"
                  name="messaging_product"
                  value={formData.messaging_product}
                  disabled
                  className="wa-disabled-field"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="wa-form-group">
                <Form.Label className="wa-required-label">Email Address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  placeholder="Enter business email"
                />
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="wa-form-group mb-3">
            <Form.Label className="wa-required-label">Business Address</Form.Label>
            <Form.Control
              as="textarea"
              className="wa-textarea"
              name="address"
              value={formData.address}
              onChange={handleChange}
              isInvalid={!!errors.address}
              placeholder="Enter complete business address"
              rows={3}
            />
            <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="wa-form-group mb-3">
            <Form.Label className="wa-required-label">Description</Form.Label>
            <Form.Control
              as="textarea"
              className="wa-textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              isInvalid={!!errors.description}
              placeholder="Enter business description"
              rows={2}
            />
            <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="wa-form-group mb-3">
            <Form.Label className="wa-required-label">Business Type</Form.Label>
            <Form.Control
              as="select"
              name="vertical"
              value={formData.vertical}
              onChange={handleChange}
              isInvalid={!!errors.vertical}
              className="wa-select"
            >
              <option value="">Select Business Type</option>
              {BUSINESS_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">{errors.vertical}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="wa-form-group mb-3">
            <Form.Label className="wa-required-label">About</Form.Label>
            <Form.Control
              as="textarea"
              className="wa-textarea"
              name="about"
              value={formData.about}
              onChange={handleChange}
              isInvalid={!!errors.about}
              placeholder="Enter information about your business"
              rows={2}
            />
            <Form.Control.Feedback type="invalid">{errors.about}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="wa-form-group mb-3">
            <Form.Label>Website URL</Form.Label>
            <Form.Control
              type="url"
              name="websites"
              value={formData.websites}
              onChange={handleChange}
              isInvalid={!!errors.websites}
              placeholder="Enter business website URL (https://...)"
            />
            <Form.Text className="text-muted">Must include https://</Form.Text>
            <Form.Control.Feedback type="invalid">{errors.websites}</Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={onHide} className="wa-button-secondary">
            Cancel
          </Button>
          <Button variant="primary" type="submit" className="wa-button-primary">
            Save Changes
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UpdateBusinessModal;
